import { DIMENSIONS } from '@dce-front/onewebapp-dive-utils';
import classNames from 'classnames';
import type { JSX } from 'react';
import { memo, useMemo } from 'react';
import { ImageType } from '../ImageType/ImageType';
import styles from './ProgramsHighlight.module.css';
import { ProgramsHighlightTv } from './ProgramsHighlightTv/ProgramsHighlightTv';
import type { ProgramsHighlightProps } from './types';
import { ButtonIcon, PlayerPlaySvg } from '@dce-front/dive';

function ProgramsHighlightComponent({
  content,
  isTvDevice,
  resizeMode,
  trackingContext,
  Linker,
  renderContentImageLinker,
}: ProgramsHighlightProps): JSX.Element {
  const {
    button,
    description,
    diffusionLabel,
    isPlayImage,
    title,
    onClick,
    URLImage,
  } = content || {};

  const { onClick: mainOnClick } = button || {};

  const URLImageObject = useMemo(
    () => ({
      default: URLImage,
    }),
    [URLImage],
  );

  if (isTvDevice) {
    return (
      <ProgramsHighlightTv
        Linker={Linker}
        img={
          <ImageType
            dimensions={{ default: DIMENSIONS.PROGRAMS_HIGHLIGHT }}
            URLImage={URLImageObject}
            isArtDirection
            isTvDevice
            resizeMode={resizeMode}
          />
        }
        renderLinker={renderContentImageLinker}
        content={content}
        trackingContext={trackingContext}
      />
    );
  }

  const img = (
    <>
      <ImageType
        dimensions={DIMENSIONS.PROGRAMS_HIGHLIGHT}
        URLImage={URLImageObject}
        isArtDirection
      />
      {/* if isPlayImage is true, add a fake play button in the middle of the image, not for TV */}
      {isPlayImage && (
        <div
          className={styles.ProgramsHighlight__mediaWrap__overlay}
          role="presentation"
        >
          <ButtonIcon
            as="div"
            className={styles.ProgramsHighlight__mediaWrap__play}
            icon={<PlayerPlaySvg />}
            variant="media-overlay"
          />
        </div>
      )}
    </>
  );

  return (
    <div
      className={classNames(styles.ProgramsHighlight, 'programsHighlightFocus')}
    >
      {/* IMAGE */}
      <div className={styles.ProgramsHighlight__mediaWrap}>
        {onClick
          ? renderContentImageLinker({
              content,
              children: img,
              id: 'highlight_mediaWrap',
              classNames: styles.ProgramsHighlight__mediaWrap__linker,
              trackingContext,
            })
          : img}
      </div>
      {/* CTA */}
      <div className={styles.ProgramsHighlight__ctaWrap}>
        {diffusionLabel && (
          <h2 className={styles.ProgramsHighlight__diffusionLabel}>
            {diffusionLabel}
          </h2>
        )}
        {title && (
          <span className={styles.ProgramsHighlight__title}>{title}</span>
        )}
        {description && (
          <p className={styles.ProgramsHighlight__description}>{description}</p>
        )}
        {button && (
          <Linker
            className={styles.ProgramsHighlight__button}
            data={{
              mainOnClick: { ...mainOnClick, trackingContext },
            }}
            objKey="onClick"
          >
            {button.label}
          </Linker>
        )}
      </div>
    </div>
  );
}

export const ProgramsHighlight = memo(ProgramsHighlightComponent);
